import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
  Icon,
  useMediaQuery,
  useTheme
} from '@mui/material';

export const SessionWarningDialog = ({ open, countdownSeconds, onKeepAlive, onLogout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [progress, setProgress] = useState(100);
  
  const totalSeconds = 120;
  const minutes = Math.floor(countdownSeconds / 60);
  const seconds = countdownSeconds % 60;
  const isUrgent = countdownSeconds <= 30;
  
  useEffect(() => {
    if (open && countdownSeconds > 0) {
      const newProgress = (countdownSeconds / totalSeconds) * 100;
      setProgress(newProgress);
    }
  }, [open, countdownSeconds]);
  
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
          maxWidth: 400,
          width: "100%",
          m: isMobile ? 2 : "auto",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          height: 5,
          background: isUrgent
            ? "linear-gradient(90deg, #ef4444, #dc2626)"
            : "linear-gradient(90deg, #219297, #1a7a7d)",
          width: `${progress}%`,
          transition: "width 1s linear",
        }}
      />
      
      <Box sx={{ p: isMobile ? 3 : 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            backgroundColor: isUrgent ? "#fef2f2" : "#f0fdfa",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
            border: `2px solid ${isUrgent ? "#fecaca" : "#ccfbf1"}`,
          }}
        >
          <Icon sx={{ fontSize: 36, color: isUrgent ? "#ef4444" : "#219297" }}>
            {isUrgent ? "warning" : "schedule"}
          </Icon>
        </Box>
        
        <Typography variant="h5" fontWeight="700" sx={{ mb: 1 }}>
          Session Expiring Soon
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You've been inactive. Your session will expire in:
        </Typography>
        
        <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={100}
            thickness={4}
            sx={{ color: "#e5e7eb", position: "absolute" }}
          />
          <CircularProgress
            variant="determinate"
            value={progress}
            size={100}
            thickness={4}
            sx={{ color: isUrgent ? "#ef4444" : "#219297" }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h5"
              fontWeight="800"
              sx={{ color: isUrgent ? "#ef4444" : "#219297" }}
            >
              {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: "10px" }}>
              remaining
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={1.5} flexDirection={isMobile ? "column" : "row"}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onLogout}
            sx={{
              py: 1.25,
              fontWeight: 600,
              textTransform: "none",
              color: "#6b7280",
              borderColor: "#d1d5db",
              borderRadius: "10px",
            }}
          >
            Log Out
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onKeepAlive}
            sx={{
              py: 1.25,
              fontWeight: 700,
              textTransform: "none",
              background: isUrgent
                ? "linear-gradient(135deg, #ef4444, #dc2626)"
                : "linear-gradient(135deg, #219297, #1a7a7d)",
              color: "#ffffff",
              borderRadius: "10px",
              "&:hover": { transform: "translateY(-1px)" },
            }}
            startIcon={<Icon sx={{ fontSize: 18 }}>refresh</Icon>}
          >
            Keep Me Signed In
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};